import { IconCircleCheckFilled } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { Card, Radio, RadioGroup } from '@cfa/react-core';

import { cn } from '@/utils/tailwind';

import PathwayModal from '@/components/popups/PathwayModal';

import {
  oppositeLanguage,
  type QuizLocale,
  type V2QuizEditorState,
} from '../state';
import { isTrueFalseQuestion } from '../v2QuizValidation';

interface ViewQuizTranslationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  quiz: V2QuizEditorState;
  /** Locale to preview (opposite of the user's Settings language). */
  viewLanguage: QuizLocale;
}

interface ReadOnlyAnswer {
  text: string;
  correct: boolean;
}

const ViewQuizTranslationsModal: React.FC<ViewQuizTranslationsModalProps> = ({
  isOpen,
  onClose,
  quiz,
  viewLanguage,
}) => {
  const { t } = useTranslation();
  const fallbackLanguage = oppositeLanguage(viewLanguage);

  return (
    <PathwayModal
      isOpen={isOpen}
      onClose={onClose}
      className="lg:max-w-[840px]!"
    >
      <PathwayModal.Header>
        <PathwayModal.Title>{t('TrainingPlans.preview')}</PathwayModal.Title>
        <p className="mb-4 text-center text-4xl font-semibold">
          {quiz.name[viewLanguage] ?? ''}
        </p>
      </PathwayModal.Header>
      <PathwayModal.Body>
        <div className="flex flex-col gap-4 text-left">
          {quiz.questions.map(question => {
            const isTrueFalse = isTrueFalseQuestion(question);
            const answers: ReadOnlyAnswer[] = question.answers.map(answer => ({
              text: isTrueFalse
                ? (answer.answer[viewLanguage] ??
                  answer.answer[fallbackLanguage] ??
                  '')
                : (answer.answer[viewLanguage] ?? ''),
              correct: answer.correct === true,
            }));
            const correctIndex = answers.findIndex(answer => answer.correct);

            return (
              <ReadOnlyQuestionCard
                key={question.clientId}
                questionText={question.question[viewLanguage] ?? ''}
                answers={answers}
                correctIndex={correctIndex}
              />
            );
          })}
        </div>
      </PathwayModal.Body>
    </PathwayModal>
  );
};

const ReadOnlyQuestionCard: React.FC<{
  questionText: string;
  answers: ReadOnlyAnswer[];
  correctIndex: number;
}> = ({ questionText, answers, correctIndex }) => {
  const { t } = useTranslation();
  const emptyLabel = t(
    'TrainingPlans.manageTranslations.noExistingTranslation',
  );

  return (
    <Card.Root outlined className="overflow-hidden shadow-md!">
      <Card.Content className="flex flex-col gap-4! p-5!">
        <p className="text-grayscale-gray6 dark:text-contrast text-base font-medium">
          {questionText || emptyLabel}
        </p>
        <RadioGroup
          value={correctIndex >= 0 ? String(correctIndex) : ''}
          orientation="vertical"
          className="w-full! gap-2!"
        >
          {answers.map((answer, answerIndex) => (
            <div
              key={answerIndex}
              className={cn(
                'flex items-center justify-between gap-3 rounded border border-solid border-transparent p-1',
                answer.correct &&
                  'border-feedback-success-base bg-feedback-success-container',
              )}
            >
              <Radio
                value={String(answerIndex)}
                isDisabled
                className={cn(
                  'text-grayscale-gray7! dark:text-contrast flex-1 cursor-default! text-left text-base! font-normal!',
                  answer.correct &&
                    '[&::before]:border-feedback-success-base! [&::before]:border-[6px]!',
                )}
              >
                {answer.text || emptyLabel}
              </Radio>
              {answer.correct && (
                <span className="text-feedback-success-base flex shrink-0 items-center gap-2 text-base font-semibold">
                  {t('TrainingPlans.previewQuiz.correctAnswer')}
                  <IconCircleCheckFilled className="size-6" />
                </span>
              )}
            </div>
          ))}
        </RadioGroup>
      </Card.Content>
    </Card.Root>
  );
};

export default ViewQuizTranslationsModal;
